import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { reviewService, IReviewPoll } from './service/review.service';
import { JoinForm } from './components/join-form.component';
import { QueueList } from './components/queue-list.component';
import { ChatSidebar, ChatToggleButton } from './components/chat-sidebar.component';
import { MeetingStatusPill } from './components/meeting-status.component';
import { DiscordPill, WebhookPill } from '../common/components/discord-pill.component';
import { useNotifications } from './hooks/use-notifications';
import { usePushSubscription } from './hooks/use-push-subscription';
import { useReviewSocket } from './hooks/use-review-socket';
import useAuth from '../auth/feature/auth';
import { teamService, ITeam } from '../team/service/team.service';

const PARTICIPANT_TOKEN_PREFIX = 'standby_participant_';

const WaitCountdown = ({ estimatedMinutes }: { estimatedMinutes: number }) => {
  const [secondsLeft, setSecondsLeft] = React.useState(estimatedMinutes * 60);
  const startRef = React.useRef(Date.now());
  const initialRef = React.useRef(estimatedMinutes * 60);

  // Reset when estimate changes (WebSocket update)
  React.useEffect(() => {
    startRef.current = Date.now();
    initialRef.current = estimatedMinutes * 60;
    setSecondsLeft(estimatedMinutes * 60);
  }, [estimatedMinutes]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
      const remaining = Math.max(0, initialRef.current - elapsed);
      setSecondsLeft(remaining);
    }, 1000);
    return () => clearInterval(interval);
  }, [estimatedMinutes]);

  if (secondsLeft <= 0) return <span>any moment now</span>;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  if (mins === 0) return <span>{secs}s wait</span>;
  return <span>{mins}:{secs.toString().padStart(2, '0')} wait</span>;
};

const CheckinQrCard = ({ checkinToken }: { checkinToken: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const qrUrl = `${window.location.origin}/qr/${checkinToken}`;

  return (
    <>
      <div className="mb-[24px] p-[16px] rounded-[16px] border border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.06)] bg-[#F8FCFF] dark:bg-[#28282B]">
        <div className="flex items-center justify-between mb-[12px]">
          <p className="font-body text-[13px] font-medium text-[#818185] dark:text-[#A0A0A3]">
            {t('review.checkinQrTitle', 'Your check-in QR code')}
          </p>
          <button
            onClick={() => setExpanded(true)}
            className="text-[12px] font-medium text-[#0ea5c9] hover:text-[#0CB3D8] transition-colors"
          >
            {t('review.checkinQrFullscreen', 'Fullscreen')}
          </button>
        </div>
        <div className="flex justify-center">
          <div className="bg-white rounded-[8px] p-[8px]">
            <QRCodeSVG value={qrUrl} size={120} level="M" />
          </div>
        </div>
        <p className="font-body text-[11px] text-[#818185] dark:text-[#6B6B6F] text-center mt-[8px]">
          {t('review.checkinQrHint', 'Show this to the queue admin to check in')}
        </p>
      </div>

      {expanded && (
        <div className="fixed inset-0 z-[200] bg-black flex items-center justify-center" onClick={() => setExpanded(false)}>
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-[20px] right-[20px] w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div className="flex flex-col items-center gap-[24px]" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white rounded-[16px] p-[24px]">
              <QRCodeSVG value={qrUrl} size={280} level="M" />
            </div>
            <p className="font-body text-[13px] text-white/70 text-center">
              {t('review.checkinQrHint', 'Show this to the queue admin to check in')}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const JoinNameForm = ({ taken, onSubmit, isPending }: { taken: { name: string }; onSubmit: (name: string) => void; isPending: boolean }) => {
  const { t } = useTranslation();
  const [customName, setCustomName] = React.useState('');
  return (
    <form
      className="flex gap-[8px] items-center"
      onSubmit={(e) => {
        e.preventDefault();
        if (customName.trim() && customName.trim() !== taken.name) onSubmit(customName.trim());
      }}
    >
      <input
        value={customName}
        onChange={(e) => setCustomName(e.target.value)}
        maxLength={50}
        placeholder={t('participantCard.nameTakenPlaceholder')}
        className="flex-1 min-w-0 h-[40px] px-[14px] text-[14px] font-body text-base-black bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#0CB3D8] focus:shadow-[0_0_0_3px_rgba(93,225,255,0.20)] transition-all"
      />
      <button
        type="submit"
        disabled={isPending || !customName.trim() || customName.trim() === taken.name}
        className={`h-[40px] px-[16px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all flex-shrink-0 ${
          isPending || !customName.trim() || customName.trim() === taken.name
            ? 'bg-gray-900 opacity-50'
            : 'bg-base-black dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#29292A] dark:hover:bg-[#D8D8DB]'
        }`}
      >
        {t('joinForm.join')}
      </button>
    </form>
  );
};

export const ReviewPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const [chatOpen, setChatOpen] = React.useState(false);

  const tokenKey = `${PARTICIPANT_TOKEN_PREFIX}${slug}`;
  const stored = localStorage.getItem(tokenKey);
  const parsedStored = stored ? JSON.parse(stored) : null;
  const [myParticipantId, setMyParticipantId] = React.useState<number | null>(
    parsedStored?.id ?? null
  );
  const [myName, setMyName] = React.useState<string>(parsedStored?.name ?? '');
  const myPreviousNamesRef = React.useRef<Set<string>>(new Set(parsedStored?.name ? [parsedStored.name] : []));
  const [joinNameTaken, setJoinNameTaken] = React.useState<{ name: string; suggestions: string[]; password?: string } | null>(null);

  const {
    data: review,
    isLoading,
    error
  } = useQuery<IReviewPoll | void>({
    queryKey: ['review-poll', slug],
    queryFn: () => reviewService.pollReview(slug!),
    refetchInterval: false,
    retry: 1,
    enabled: !!slug
  });

  const { data: isAdmin } = useQuery<boolean | void>({
    queryKey: ['is-admin', slug],
    queryFn: () => reviewService.isUserAdmin(slug!),
    enabled: !!slug && !!user
  });

  const [myCheckinToken, setMyCheckinToken] = React.useState<string | null>(parsedStored?.checkinToken ?? null);

  useReviewSocket(slug, {
    onCheckinTokens: (tokens) => {
      const mine = tokens.find((t) => t.participantId === myParticipantId);
      if (mine) {
        setMyCheckinToken(mine.checkinToken);
        const s = localStorage.getItem(tokenKey);
        const parsed = s ? JSON.parse(s) : {};
        localStorage.setItem(tokenKey, JSON.stringify({ ...parsed, checkinToken: mine.checkinToken }));
      }
    },
    onCheckinTokensCleared: () => {
      setMyCheckinToken(null);
      const s = localStorage.getItem(tokenKey);
      const parsed = s ? JSON.parse(s) : {};
      delete parsed.checkinToken;
      localStorage.setItem(tokenKey, JSON.stringify(parsed));
    },
  });

  const saveParticipant = (id: number, token: string, name: string, checkinToken?: string | null) => {
    setMyParticipantId(id);
    setMyName(name);
    if (checkinToken) setMyCheckinToken(checkinToken);
    localStorage.setItem(tokenKey, JSON.stringify({ id, token, name, checkinToken: checkinToken || undefined }));
  };

  // Auto-reconnect: match by userId, stored ID, or stored name
  React.useEffect(() => {
    if (!review || myParticipantId) return;
    const participants = review.participants || [];

    // Try logged-in user match by userId
    if (isAuthenticated && user?.id) {
      const byUserId = participants.find((p) => p.userId === user.id);
      if (byUserId) {
        saveParticipant(byUserId.id, '', byUserId.name);
        return;
      }
    }

    // Try stored name match (returning user or fallback after join)
    const storedNow = localStorage.getItem(tokenKey);
    const parsed = storedNow ? JSON.parse(storedNow) : null;
    if (parsed?.name) {
      const byName = participants.find(
        (p) => p.name.toLowerCase() === parsed.name.toLowerCase()
      );
      if (byName) {
        saveParticipant(byName.id, parsed.token || '', byName.name);
      }
    }
  }, [review, myParticipantId, isAuthenticated, user]);

  // Sync myName when participant renames (via WebSocket review update)
  React.useEffect(() => {
    if (!review || !myParticipantId) return;
    const me = (review.participants || []).find((p) => p.id === myParticipantId);
    if (me && me.name !== myName) {
      myPreviousNamesRef.current.add(myName);
      myPreviousNamesRef.current.add(me.name);
      setMyName(me.name);
      const storedNow = localStorage.getItem(tokenKey);
      const parsed = storedNow ? JSON.parse(storedNow) : null;
      if (parsed) {
        localStorage.setItem(tokenKey, JSON.stringify({ ...parsed, name: me.name }));
      }
    }
  }, [review, myParticipantId]);

  const { mutate: join, isPending: joinPending } = useMutation({
    mutationFn: ({ name, password }: { name: string; password?: string }) =>
      reviewService.joinReview(slug!, name, password),
    onSuccess: (data, vars) => {
      if (data && data.id) {
        saveParticipant(data.id, data.token, vars.name, data.checkinToken);
      } else {
        setMyName(vars.name);
        localStorage.setItem(tokenKey, JSON.stringify({ id: null, token: '', name: vars.name }));
      }
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
      queryClient.invalidateQueries({ queryKey: ['my-participating-reviews'] });
    },
    onError: (err: any, vars) => {
      const data = err.response?.data;
      if (data?.code === 'NAME_TAKEN' && Array.isArray(data.suggestions)) {
        setJoinNameTaken({ name: vars.name, suggestions: data.suggestions, password: vars.password });
        return;
      }
      const msg = data?.message;
      const detail = Array.isArray(msg) ? msg[0] : msg;
      toast.error(detail || t('review.joinFailed'));
    }
  });

  const { mutate: leaveQueue, isPending: leavePending } = useMutation({
    mutationFn: () => reviewService.leaveReview(slug!, myParticipantId!),
    onSuccess: () => {
      setMyParticipantId(null);
      setMyName('');
      localStorage.removeItem(tokenKey);
      queryClient.invalidateQueries({ queryKey: ['review-poll', slug] });
      queryClient.removeQueries({ queryKey: ['my-participating-reviews'] });
      queryClient.removeQueries({ queryKey: ['admin-reviews'] });
      toast.success(t('review.leftQueue'));
      navigate('/');
    },
    onError: () => toast.error(t('review.leaveFailed'))
  });

  const [showLeaveModal, setShowLeaveModal] = React.useState(false);

  // Fetch user's teams to check membership for team pill navigation
  const { data: myTeams } = useQuery<ITeam[]>({
    queryKey: ['my-teams'],
    queryFn: () => teamService.getMyTeams() as Promise<ITeam[]>,
    enabled: isAuthenticated === true
  });
  const myTeamIds = React.useMemo(() => new Set((myTeams || []).map((t) => t.id)), [myTeams]);

  // If signed in and already joined, link userId to participant for dashboard visibility (once)
  const hasLinkedRef = React.useRef(false);
  React.useEffect(() => {
    if (isAuthenticated && myParticipantId && slug && !hasLinkedRef.current) {
      hasLinkedRef.current = true;
      reviewService.linkParticipant(slug, myParticipantId).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['my-participating-reviews'] });
    }
  }, [isAuthenticated, myParticipantId, slug]);

  usePushSubscription({ participantId: myParticipantId, participantToken: parsedStored?.token || null, userId: user?.id || null, slug: slug || '' });

  // Apply company branding when review belongs to a company
  React.useEffect(() => {
    if (!review?.company?.primaryColor) return;
    const root = document.documentElement;
    root.style.setProperty('--color-brand-primary', review.company.primaryColor);
    return () => {
      root.style.setProperty('--color-brand-primary', '#0ea5c9');
    };
  }, [review?.company?.primaryColor]);

  // Browser notifications for status changes and @mentions
  const { unreadCount } = useNotifications({
    review: review || null,
    myParticipantId,
    myName,
    slug: slug || '',
    chatOpen
  });

  if (isLoading && !error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-[#1D1D1E] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !review || !review.isActive) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-[24px]">
        <h1 className="font-display text-[28px] font-bold text-base-black mb-[8px]">
          {t('review.reviewEndedTitle')}
        </h1>
        <p className="font-body text-[16px] text-gray-900">
          {t('review.reviewEndedDescription')}
        </p>
      </div>
    );
  }

  const hasJoined = myParticipantId !== null;
  const myParticipant = review.participants?.find((p) => p.id === myParticipantId);
  const meetingHref = review.meetingUrl && !/^https?:\/\//i.test(review.meetingUrl)
    ? `https://${review.meetingUrl}`
    : review.meetingUrl;

  return (
    <div className="max-w-[600px] mx-auto py-[32px] md:py-[48px]">
      {review.company && (
        <div className="flex items-center justify-center gap-[8px] mb-[16px]">
          {review.company.logoUrl && <img src={review.company.logoUrl} alt="" className="w-[20px] h-[20px] rounded-[4px] object-cover" />}
          <span className="font-body text-[13px] font-medium" style={{ color: review.company.primaryColor || '#0ea5c9' }}>{review.company.name}</span>
        </div>
      )}
      <div className="flex items-center justify-between mb-[8px]">
        <Link to="/dashboard" className="inline-flex items-center gap-[4px] font-body text-[13px] font-medium text-[#818185] hover:text-base-black transition-colors">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          {t('header.dashboard')}
        </Link>
        {isAdmin && (
          <Link
            to={`/review/${slug}/admin`}
            className="inline-flex items-center gap-[6px] h-[32px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[12px] font-medium text-gray-900 dark:text-[#A0A0A3] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] hover:text-base-black dark:hover:text-white transition-all"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            {t('review.viewAsAdmin')}
          </Link>
        )}
      </div>
      <div className="text-center mb-[32px]">
        <h1 className="font-display text-[24px] md:text-[32px] font-bold text-base-black mb-[4px]">
          {review.title}
        </h1>
        {review.isActive && (
          <div className="flex items-center justify-center gap-[8px] flex-wrap">
            <MeetingStatusPill
              started={!!review.meetingStarted}
              isAdmin={false}
            />
            {review.teams?.length > 0 && review.teams.map((team) => (
              <React.Fragment key={team.id}>
                {isAuthenticated && myTeamIds.has(team.id) ? (
                  <Link
                    to={`/team/${team.id}`}
                    className="inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-[#4A4A4C] text-[12px] font-semibold hover:bg-[#E0E0E3] transition-all"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    {team.name}
                  </Link>
                ) : (
                  <span className="inline-flex items-center gap-[5px] px-[10px] py-[3px] rounded-[5px] bg-[#EFEFF1] text-[#4A4A4C] text-[12px] font-semibold">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    {team.name}
                  </span>
                )}
                <span className="text-[#BFBFC0]">&middot;</span>
              </React.Fragment>
            ))}
            {review.hasDiscordWebhook && (
              <>
                <DiscordPill channelUrl={review.discordChannelUrl} />
                <span className="text-[#BFBFC0]">&middot;</span>
              </>
            )}
            {review.hasSlackWebhook && (
              <>
                <WebhookPill provider="slack" channelUrl={review.slackChannelUrl} />
                <span className="text-[#BFBFC0]">&middot;</span>
              </>
            )}
            <p className="font-body text-[14px] text-gray-900">
              {review.participants?.length || 0} participant
              {(review.participants?.length || 0) !== 1 ? 's' : ''}             </p>
          </div>
        )}
        {meetingHref && (
          <a
            href={meetingHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-[6px] mt-[12px] h-[36px] px-[16px] bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] rounded-[32px] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all max-w-[240px]"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </svg>
            <span className="truncate">{review.meetingLinkLabel || t('review.joinReview')}</span>
          </a>
        )}
        {hasJoined && (
          <button
            type="button"
            onClick={() => setShowLeaveModal(true)}
            className="mt-[12px] h-[32px] px-[14px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[12px] font-medium text-gray-900 dark:text-[#A0A0A3] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] hover:text-base-black dark:hover:text-white transition-all flex items-center gap-[6px] mx-auto"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {t('review.leaveQueue')}
          </button>
        )}
      </div>

      <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] p-[16px] md:p-[24px] shadow-[0_1px_3px_rgba(0,0,0,0.07)]">

      {hasJoined && myParticipant && (() => {
        const waitingSorted = review.participants
          .filter((p) => p.status === 'waiting')
          .sort((a, b) => a.position - b.position);
        const myQueueIndex = waitingSorted.findIndex((p) => p.id === myParticipant.id);
        const statusStyle = myParticipant.status === 'active'
          ? 'bg-green-500 text-green-700'
          : myParticipant.status === 'done'
            ? 'bg-gray-400 text-gray-700'
            : myQueueIndex === 0
              ? 'bg-orange-400 text-orange-800'
              : myQueueIndex === 1
                ? 'bg-yellow-400 text-yellow-700'
                : 'bg-gray-300 text-gray-600';
        const statusText = myParticipant.status === 'active'
          ? t('review.statusActive')
          : myParticipant.status === 'done'
            ? t('review.statusDone')
            : myQueueIndex === 0
              ? t('review.statusNextUp')
              : myQueueIndex === 1
                ? t('review.statusStandby')
                : t('review.statusWaiting', { position: myQueueIndex + 1 });
        const myEstimate = (review as any).waitEstimate?.estimates?.find((e: any) => e.participantId === myParticipant.id);
        return (
          <div className={`mb-[24px] p-[16px] rounded-[16px] ${statusStyle}`}>
            <p className="font-body text-[14px] text-center font-semibold">
              {statusText}
            </p>
            {myEstimate && myParticipant.status === 'waiting' && (review.featureToggles?.waitEstimates !== false) && (
              <p className="font-body text-[12px] text-center opacity-80 mt-[4px]">
                ~<WaitCountdown estimatedMinutes={myEstimate.estimatedMinutes} />
              </p>
            )}
          </div>
        );
      })()}

      {/* QR Check-in code for participants in company queues */}
      {hasJoined && myParticipant && myCheckinToken && myParticipant.status === 'waiting' && review.featureToggles?.qrCheckin === true && (
        <CheckinQrCard checkinToken={myCheckinToken} />
      )}

      {!hasJoined && review.isActive && (
        <div className="mb-[24px]">
          <JoinForm
            onJoin={(name, password) => join({ name, password })}
            isLoading={joinPending}
            requiresPassword={!!review.hasPassword}
            defaultName={user?.displayName || user?.email?.split('@')[0] || ''}
          />
        </div>
      )}

      <QueueList
        participants={review.participants || []}
        myParticipantId={myParticipantId ?? undefined}
        slug={slug}
        waitEstimates={(review.featureToggles?.waitEstimates !== false) ? (review as any).waitEstimate?.estimates : undefined}
        privacyMode={review.featureToggles?.privacyMode === true}
      />

      </div>

      {hasJoined && myName && slug && (review.featureToggles?.chat !== false) && (
        <>
          <ChatToggleButton onClick={() => setChatOpen(true)} unreadCount={unreadCount} />
          <ChatSidebar
            slug={slug}
            senderName={myName}
            senderPreviousNames={myPreviousNamesRef.current}
            participants={review.participants || []}
            isTeamReview={review.teams?.length > 0}
            adminNames={review.adminNames || []}
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
          />

        </>
      )}

      {joinNameTaken && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setJoinNameTaken(null); }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px]">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black mb-[4px]">
                {t('participantCard.nameTakenTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] leading-[1.5]">
                {t('participantCard.nameTakenMessage', { name: joinNameTaken.name })}
              </p>
            </div>

            <p className="font-body text-[12px] text-[#818185] dark:text-[#A0A0A3] font-medium">
              {t('participantCard.nameTakenSuggestions')}
            </p>
            <div className="flex flex-col gap-[8px]">
              {joinNameTaken.suggestions.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    const pw = joinNameTaken.password;
                    setJoinNameTaken(null);
                    join({ name: s, password: pw });
                  }}
                  disabled={joinPending}
                  className={`w-full h-[44px] rounded-[12px] border font-body text-[14px] text-left px-[16px] disabled:opacity-50 transition-all ${
                    i === 0
                      ? 'border-[#0CB3D8] bg-[#F8FCFF] dark:bg-[#0ea5c9]/10 font-medium text-base-black dark:text-white hover:bg-[#EDF9FF] dark:hover:bg-[#0ea5c9]/15'
                      : 'border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] bg-[#F5F5F6] dark:bg-[#28282B] font-normal text-base-black dark:text-[#A0A0A3] hover:border-[#0CB3D8] hover:bg-[#F8FCFF] dark:hover:bg-[#0ea5c9]/10 dark:hover:text-white'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="border-t border-[#E5E5ED] dark:border-[rgba(255,255,255,0.08)] pt-[12px]">
              <p className="font-body text-[12px] text-[#818185] dark:text-[#A0A0A3] font-medium mb-[8px]">
                {t('participantCard.nameTakenCustom')}
              </p>
              <JoinNameForm
                taken={joinNameTaken}
                onSubmit={(newName) => {
                  const pw = joinNameTaken.password;
                  setJoinNameTaken(null);
                  join({ name: newName, password: pw });
                }}
                isPending={joinPending}
              />
            </div>

            <button
              type="button"
              onClick={() => setJoinNameTaken(null)}
              className="w-full h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {showLeaveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowLeaveModal(false); }}
        >
          <div className="bg-white rounded-[16px] border border-[rgba(0,0,0,0.11)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px] text-center">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black mb-[4px]">
                {t('review.leaveQueue')}
              </h2>
              <p className="font-body text-[14px] text-gray-900">
                {t('review.leaveQueueConfirmMessage')}
              </p>
            </div>
            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={() => { leaveQueue(); setShowLeaveModal(false); }}
                disabled={leavePending}
                className="flex-1 h-[44px] rounded-[32px] bg-[#D8210C] text-white font-body text-[14px] font-medium hover:bg-[#b91c0a] transition-all disabled:opacity-50"
              >
                {leavePending ? t('common.leaving') : t('review.confirmLeave')}
              </button>
              <button
                type="button"
                onClick={() => setShowLeaveModal(false)}
                className="flex-1 h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
